import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    DoneAll as CompleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import returnService from '../../../services/returnService';
import { useCurrency } from '../../../hooks/useCurrency';

const STATUS_COLORS = {
    PENDING: 'warning',
    APPROVED: 'info',
    REJECTED: 'error',
    COMPLETED: 'success',
};

const ReturnDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();
    const [ret, setRet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [adminNote, setAdminNote] = useState('');
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');

    useEffect(() => {
        fetchReturn();
    }, [id]);

    const fetchReturn = async () => {
        setLoading(true);
        try {
            const data = await returnService.getReturnById(id);
            setRet(data);
        } catch (error) {
            toast.error(t('returns.fetchError'));
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async () => {
        try {
            await returnService.approveReturn(id, adminNote || undefined);
            toast.success(t('returns.approveSuccess'));
            fetchReturn();
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.saveError'));
        }
    };

    const handleReject = async () => {
        if (!rejectionReason.trim()) return;
        try {
            await returnService.rejectReturn(id, rejectionReason);
            toast.success(t('returns.rejectSuccess'));
            setRejectDialogOpen(false);
            setRejectionReason('');
            fetchReturn();
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.saveError'));
        }
    };

    const handleComplete = async () => {
        try {
            await returnService.completeReturn(id);
            toast.success(t('returns.completeSuccess'));
            fetchReturn();
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.saveError'));
        }
    };

    const { format: formatCurrency } = useCurrency();

    const formatDate = (dateString) =>
        dateString ? new Date(dateString).toLocaleString('fr-FR') : '-';

    if (loading) {
        return <Box sx={{ p: 3, textAlign: 'center' }}><Typography>{t('common.loading')}</Typography></Box>;
    }
    if (!ret) {
        return <Box sx={{ p: 3 }}><Alert severity="error">{t('returns.notFound')}</Alert></Box>;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/returns')}>
                        {t('common.back')}
                    </Button>
                    <Typography variant="h4">{ret.returnNumber}</Typography>
                    <Chip label={ret.statusLabel} color={STATUS_COLORS[ret.status] || 'default'} />
                </Box>

                {ret.status === 'PENDING' && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" color="success" startIcon={<ApproveIcon />} onClick={handleApprove}>
                            {t('returns.approve')}
                        </Button>
                        <Button variant="outlined" color="error" startIcon={<RejectIcon />} onClick={() => setRejectDialogOpen(true)}>
                            {t('returns.reject')}
                        </Button>
                    </Box>
                )}
                {ret.status === 'APPROVED' && (
                    <Button variant="contained" startIcon={<CompleteIcon />} onClick={handleComplete}>
                        {t('returns.complete')}
                    </Button>
                )}
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>{t('orders.order')}</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={1.5}>
                                <Grid item xs={5}><Typography color="text.secondary">{t('orders.order')}</Typography></Grid>
                                <Grid item xs={7}>
                                    <Typography
                                        sx={{ cursor: 'pointer', color: 'primary.main' }}
                                        onClick={() => navigate(`/orders/${ret.orderId}`)}
                                    >
                                        {ret.orderNumber}
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}><Typography color="text.secondary">{t('customers.customer')}</Typography></Grid>
                                <Grid item xs={7}><Typography>{ret.customerName || '-'}</Typography></Grid>
                                <Grid item xs={5}><Typography color="text.secondary">{t('common.email')}</Typography></Grid>
                                <Grid item xs={7}><Typography>{ret.customerEmail || '-'}</Typography></Grid>
                                <Grid item xs={5}><Typography color="text.secondary">{t('returns.type')}</Typography></Grid>
                                <Grid item xs={7}><Typography>{ret.typeLabel}</Typography></Grid>
                                <Grid item xs={5}><Typography color="text.secondary">{t('returns.reason')}</Typography></Grid>
                                <Grid item xs={7}><Typography>{ret.reasonLabel}</Typography></Grid>
                                <Grid item xs={5}><Typography color="text.secondary">{t('common.date')}</Typography></Grid>
                                <Grid item xs={7}><Typography>{formatDate(ret.createdAt)}</Typography></Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>{t('returns.notes')}</Typography>
                            <Divider sx={{ mb: 2 }} />
                            {ret.customerNote && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">{t('returns.customerNote')}</Typography>
                                    <Typography variant="body2">{ret.customerNote}</Typography>
                                </Box>
                            )}
                            {ret.rejectionReason && (
                                <Alert severity="error" sx={{ mb: 2 }}>{ret.rejectionReason}</Alert>
                            )}
                            {ret.adminNote && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">{t('returns.adminNote')}</Typography>
                                    <Typography variant="body2">{ret.adminNote}</Typography>
                                </Box>
                            )}
                            {ret.stockRestored && (
                                <Alert severity="success">{t('returns.stockRestored')}</Alert>
                            )}
                            {ret.status === 'PENDING' && (
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    sx={{ mt: 2 }}
                                    label={t('returns.adminNote')}
                                    value={adminNote}
                                    onChange={(e) => setAdminNote(e.target.value)}
                                    placeholder={t('returns.adminNotePlaceholder')}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>{t('purchaseOrders.items')}</Typography>
                            <TableContainer component={Paper} sx={{ mt: 2 }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('stockMovements.product')}</TableCell>
                                            <TableCell>{t('stockMovements.variant')}</TableCell>
                                            <TableCell align="right">{t('common.quantity')}</TableCell>
                                            <TableCell align="right">{t('common.price')}</TableCell>
                                            {ret.type === 'EXCHANGE' && <TableCell>{t('returns.exchangeFor')}</TableCell>}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(ret.items || []).map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell>{item.productName}</TableCell>
                                                <TableCell>{item.variantName}</TableCell>
                                                <TableCell align="right">{item.quantityReturned}</TableCell>
                                                <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                                                {ret.type === 'EXCHANGE' && (
                                                    <TableCell>{item.exchangeVariantName || '-'}</TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{t('returns.reject')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={3}
                        sx={{ mt: 1 }}
                        label={t('returns.rejectionReason')}
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialogOpen(false)}>{t('common.cancel')}</Button>
                    <Button onClick={handleReject} color="error" variant="contained" disabled={!rejectionReason.trim()}>
                        {t('returns.reject')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ReturnDetails;
